import {
  CompanyContributions,
  CompanyEntries,
  CompanyMetrics,
  CompanyPartners,
} from '../modules/investment/index.js';
import { CompanyAccess } from './company-access.js';
import { CompanyAudit } from './company-audit.js';
import { useCompanyScope } from './company-context.js';
import { CompanyOutbox } from './company-outbox.js';
import { EmailTemplates } from './email-templates.js';
import { WhatsAppTemplates } from './whatsapp-templates.js';
import { GatedCompanyScreen } from './gated-company-screen.js';
import type {
  AccessView,
  AuditView,
  ContributionsView,
  EntriesView,
  OutboxView,
  PartnersView,
  TemplatesView,
} from './list-views.js';
import type { ViewSetter } from './view-state.js';

/// Aporte e compra pertencem a uma unidade (ADR 0039): sem unidade escolhida numa empresa de
/// várias, a tela consolida só para consulta.
function isConsolidated(company: {
  businessUnits?: readonly unknown[] | undefined;
  activeBusinessUnitId?: string | null | undefined;
}): boolean {
  return (
    (company.businessUnits ?? []).length > 1 && (company.activeBusinessUnitId ?? null) === null
  );
}

/// Tela que a pessoa não pode ver responde como endereço inexistente, e não como "proibido".
///
/// Dizer "você não tem permissão para ver Auditoria" confirma que Auditoria existe naquela empresa
/// — e a barra lateral já esconde o que a pessoa não alcança. As duas respostas precisam contar a
/// mesma história, senão a que sobra é a que vaza.
export function CompanyAccessScreen({
  view,
  onView,
}: {
  view: AccessView;
  onView: ViewSetter<AccessView>;
}) {
  const { can, company, isRoot, user } = useCompanyScope();
  return (
    <GatedCompanyScreen allowed={can('users.read')}>
      <CompanyAccess
        currentUserId={user.id}
        unitAccess={{
          units: company.businessUnits ?? [],
          canAssign: can('roles.assign') && (isRoot || company.seesEveryUnit === true),
        }}
        capabilities={{
          canInvite: can('users.invite') && can('roles.assign'),
          canCreateWithPassword: can('users.create_with_password') && can('roles.assign'),
          canViewRoles: can('roles.read'),
          canAssignRoles: can('roles.assign'),
          canCreateRoles: can('roles.create'),
          canUpdateRoles: can('roles.update'),
          canRemoveRoles: can('roles.delete'),
          canResetAuthenticator: can('users.reset_authenticator'),
        }}
        view={view}
        onView={onView}
      />
    </GatedCompanyScreen>
  );
}

export function CompanyPartnersScreen({
  view,
  onView,
}: {
  view: PartnersView;
  onView: ViewSetter<PartnersView>;
}) {
  const { can, timeZone } = useCompanyScope();
  return (
    <GatedCompanyScreen allowed={can('partners.read')}>
      <CompanyPartners
        capabilities={{
          canCreate: can('partners.create'),
          canUpdate: can('partners.update'),
          canDeactivate: can('partners.delete'),
        }}
        timeZone={timeZone}
        view={view}
        onView={onView}
      />
    </GatedCompanyScreen>
  );
}

export function CompanyContributionsScreen({
  view,
  onView,
}: {
  view: ContributionsView;
  onView: ViewSetter<ContributionsView>;
}) {
  const { can, timeZone, today, company } = useCompanyScope();
  const consolidated = isConsolidated(company);
  const may = (permission: string) => !consolidated && can(permission);
  return (
    <GatedCompanyScreen allowed={can('investment.read')}>
      <CompanyContributions
        capabilities={{
          canCreate: may('investment.create'),
          canUpdate: may('investment.update'),
          canDelete: may('investment.delete'),
          canManageRepayments: may('investment.repayments.manage'),
        }}
        consolidated={consolidated}
        timeZone={timeZone}
        today={today}
        view={view}
        onView={onView}
      />
    </GatedCompanyScreen>
  );
}

export function CompanyEntriesScreen({
  view,
  onView,
}: {
  view: EntriesView;
  onView: ViewSetter<EntriesView>;
}) {
  const { can, timeZone, today, company } = useCompanyScope();
  const consolidated = isConsolidated(company);
  const may = (permission: string) => !consolidated && can(permission);
  return (
    <GatedCompanyScreen allowed={can('investment.read')}>
      <CompanyEntries
        capabilities={{
          canCreate: may('investment.create'),
          canUpdate: may('investment.update'),
          canDelete: may('investment.delete'),
          canSettle: may('investment.repayments.manage'),
          canManageReceipts: may('investment.receipts.manage'),
          canManageSlips: may('investment.slips.manage'),
        }}
        consolidated={consolidated}
        timeZone={timeZone}
        today={today}
        view={view}
        onView={onView}
      />
    </GatedCompanyScreen>
  );
}

export function CompanyMetricsScreen() {
  const { can } = useCompanyScope();
  return (
    <GatedCompanyScreen allowed={can('investment.read')}>
      <CompanyMetrics capabilities={{ canManageAssumptions: can('investment.settings.manage') }} />
    </GatedCompanyScreen>
  );
}

/// O recorte chega de fora porque quem sabe ler a URL é a rota, e a mesma tela pode pender de mais
/// de uma rota — a caixa de saída, por exemplo, existe na empresa e no console.
export function CompanyAuditScreen({
  view,
  onView,
}: {
  view: AuditView;
  onView: ViewSetter<AuditView>;
}) {
  const { can, timeZone } = useCompanyScope();
  return (
    <GatedCompanyScreen allowed={can('audit.read')}>
      <CompanyAudit
        timeZone={timeZone}
        canExport={can('audit.export')}
        view={view}
        onView={onView}
      />
    </GatedCompanyScreen>
  );
}

export function CompanyOutboxScreen({
  view,
  onView,
}: {
  view: OutboxView;
  onView: ViewSetter<OutboxView>;
}) {
  const { can, timeZone } = useCompanyScope();
  return (
    <GatedCompanyScreen allowed={can('email_messages.read')}>
      <CompanyOutbox timeZone={timeZone} view={view} onView={onView} />
    </GatedCompanyScreen>
  );
}

export function CompanyWhatsAppScreen() {
  const { can } = useCompanyScope();
  return (
    <GatedCompanyScreen allowed={can('whatsapp_templates.read')}>
      <WhatsAppTemplates canEdit={can('whatsapp_templates.update')} />
    </GatedCompanyScreen>
  );
}

export function CompanyTemplatesScreen({
  view,
  onView,
}: {
  view: TemplatesView;
  onView: ViewSetter<TemplatesView>;
}) {
  const { can, timeZone } = useCompanyScope();
  return (
    <GatedCompanyScreen allowed={can('email_templates.read')}>
      <EmailTemplates
        timeZone={timeZone}
        scope="TENANT"
        capabilities={{
          canEdit: can('email_templates.update'),
          canPublish: can('email_templates.publish'),
          canTest: can('email_templates.test_send'),
        }}
        view={view}
        onView={onView}
      />
    </GatedCompanyScreen>
  );
}
